/**
 * CoverageBlocksTab - Coverage Models View
 *
 * Displays pre-defined coverage model templates (Tous Risques, Tiers+, etc.)
 * with their included guarantees as shown in the mockup.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import { useQuery } from '@tanstack/react-query';
import { Plus, Shield, Edit2, Loader2 } from 'lucide-react';
import api from '../../../api.js';
import CoverageModelFormModal from '../modals/CoverageModelFormModal.jsx';
import { resolveCoverageLevel } from '../../../utils/insuranceStatus.js';

// Color mapping for coverage levels
const levelColors = {
  tous_risques: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'bg-blue-600', text: 'text-blue-700' },
  tiers_plus: { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'bg-gray-600', text: 'text-gray-700' },
  tiers_simple: { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'bg-gray-400', text: 'text-gray-600' },
  economique: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'bg-orange-500', text: 'text-orange-700' },
  custom: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'bg-purple-600', text: 'text-purple-700' },
};

function CoverageBlocksTab({ onMessage }) {
  const { t } = useTranslation();
  // Devise de l'organisation de la ligne - un seul outil monetaire.
  const { money } = useOrgCurrency();
  const [showModal, setShowModal] = useState(false);
  const [editingModel, setEditingModel] = useState(null);

  // Fetch coverage models
  const { data: modelsData, isLoading: loadingModels } = useQuery({
    queryKey: ['coverage_model'],
    queryFn: () => api.getAll('coverage_model'),
  });

  // Fetch model-guarantee links
  const { data: linksData, isLoading: loadingLinks } = useQuery({
    queryKey: ['coverage_model_guarantee'],
    queryFn: () => api.getAll('coverage_model_guarantee'),
  });

  // Fetch guarantee templates
  const { data: guaranteesData, isLoading: loadingGuarantees } = useQuery({
    queryKey: ['guarantee_template'],
    queryFn: () => api.getAll('guarantee_template'),
  });

  // Fetch vehicle insurances to count usage
  const { data: vehicleInsData } = useQuery({
    queryKey: ['vehicle_insurance'],
    queryFn: () => api.getAll('vehicle_insurance'),
  });

  const models = modelsData?.data || modelsData || [];
  const links = linksData?.data || linksData || [];
  const guarantees = guaranteesData?.data || guaranteesData || [];
  const vehicleIns = vehicleInsData?.data || vehicleInsData || [];

  // Create guarantee ID to object mapping
  const guaranteeMap = {};
  guarantees.forEach(g => {
    guaranteeMap[g.id] = g;
  });

  // Get guarantees for a model
  const getModelGuarantees = (modelId) => {
    return links
      .filter(link => link.coverage_model_id === modelId && link.inclusion_type === 'included')
      .map(link => guaranteeMap[link.guarantee_id])
      .filter(Boolean)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  };

  // Get vehicle count using this coverage level. Resolved via coverage_model_id
  // (source unique) against the loaded models — no legacy coverage string.
  const getVehicleCount = (level) => {
    return vehicleIns.filter(vi => resolveCoverageLevel(vi, models) === level).length;
  };

  // Handle open modal for new model
  const handleAddModel = () => {
    setEditingModel(null);
    setShowModal(true);
  };

  // Handle open modal for editing
  const handleEditModel = (model) => {
    setEditingModel(model);
    setShowModal(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingModel(null);
  };

  const isLoading = loadingModels || loadingLinks || loadingGuarantees;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">{t('insuranceConfig.coverageModels.loading')}</span>
      </div>
    );
  }

  // Sort models by display_order
  const sortedModels = [...models].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-gray-500">
          {t('insuranceConfig.coverageModels.intro')}
        </div>
        <button
          onClick={handleAddModel}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t('insuranceConfig.coverageModels.addButton')}
        </button>
      </div>

      {/* Models Grid */}
      {sortedModels.length === 0 ? (
        <div className="text-center py-12">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('insuranceConfig.coverageModels.emptyTitle')}</h3>
          <p className="text-gray-600 mb-4">{t('insuranceConfig.coverageModels.emptyDescription')}</p>
          <button
            onClick={handleAddModel}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
          >
            {t('insuranceConfig.coverageModels.emptyAction')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {sortedModels.map(model => {
            const colors = levelColors[model.level] || levelColors.custom;
            const modelGuarantees = getModelGuarantees(model.id);
            const vehicleCount = getVehicleCount(model.level);

            return (
              <div
                key={model.id}
                className={`border rounded-lg p-4 ${colors.border} ${colors.bg} ${
                  model.is_recommended ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className={`w-10 h-10 ${colors.icon} rounded-lg flex items-center justify-center mr-3`}>
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {model.name}
                        {model.is_recommended && (
                          <span className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                            {t('insuranceConfig.coverageModels.recommendedBadge')}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{model.short_description}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleEditModel(model)}
                    className="text-gray-400 hover:text-gray-600"
                    title={t('common.actions.edit')}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Guarantees */}
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700">{t('insuranceConfig.coverageModels.includedGuarantees')}</div>
                  <div className="flex flex-wrap gap-1">
                    {modelGuarantees.map(g => (
                      <span
                        key={g.id}
                        className={`text-xs px-2 py-1 rounded border ${
                          model.is_recommended
                            ? 'bg-white border-blue-200'
                            : 'bg-gray-100 border-gray-200'
                        }`}
                        title={g.description}
                      >
                        {g.name}
                      </span>
                    ))}
                    {modelGuarantees.length === 0 && (
                      <span className="text-xs text-gray-400 italic">{t('insuranceConfig.coverageModels.noGuaranteeLinked')}</span>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className={`mt-3 pt-3 border-t ${colors.border} text-xs ${colors.text}`}>
                  {t('insuranceConfig.coverageModels.defaultDeductible', {
                    amount: model.default_deductible
                      ? money(model.default_deductible, model)
                      : t('insuranceConfig.coverageModels.deductibleUndefined'),
                  })}
                  {vehicleCount > 0 && (
                    <span className="ml-2">{t('insuranceConfig.coverageModels.vehiclesUsing', { count: vehicleCount })}</span>
                  )}
                </div>
              </div>
            );
          })}

          {/* Add New Card */}
          <div
            onClick={handleAddModel}
            className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-600 cursor-pointer transition-colors min-h-[200px]"
          >
            <div className="text-center">
              <Plus className="w-8 h-8 mx-auto mb-2" />
              <div className="text-sm font-medium">{t('insuranceConfig.coverageModels.addCard')}</div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      <CoverageModelFormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        model={editingModel}
        onMessage={onMessage}
      />
    </div>
  );
}

export default CoverageBlocksTab;
